import { useRef } from 'react';
import { clampColumnWidth, MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from '../lib/column-widths';
import { useTranslation } from 'react-i18next';

const KEYBOARD_STEP = 24;

export interface ColumnResizeHandleProps {
  /** The column's header text, so the control can name what it resizes. */
  label: string;
  width: number;
  onResize: (width: number) => void;
  /** Back to the schema's default width. */
  onReset: () => void;
  /**
   * This column's own floor. The icon-only columns ship narrower than a text
   * column's minimum, so a single global floor would widen them on first drag.
   */
  minWidth?: number;
}

/**
 * The grab strip on a column's right border.
 *
 * Wider than it looks: the visible line is a hairline, but the hit area is 7px so
 * it can actually be caught with a mouse. It only tints on hover, because a column
 * border that is always coloured reads as a selection.
 *
 * Tracked on `document` rather than on the strip, so a fast drag that outruns the
 * pointer keeps resizing instead of stopping the moment the cursor leaves the 7px.
 */
export function ColumnResizeHandle({
  label,
  width,
  onResize,
  onReset,
  minWidth = MIN_COLUMN_WIDTH,
}: ColumnResizeHandleProps) {
  const { t } = useTranslation('projects');
  // Read at mousedown and held in a ref: `width` from props is a render behind
  // during a drag, so measuring against it would make the column drift.
  const startRef = useRef({ x: 0, width: 0 });
  /**
   * Whether a frame is already queued.
   *
   * A boolean rather than the frame id, because the id is only assigned after
   * `requestAnimationFrame` returns — so a callback that runs before then would
   * leave the guard set and swallow every later move.
   */
  const isFrameQueued = useRef(false);

  const beginDrag = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    startRef.current = { x: event.clientX, width };

    let latestX = event.clientX;
    const apply = () =>
      onResize(clampColumnWidth(startRef.current.width + (latestX - startRef.current.x), minWidth));

    /**
     * Coalesced to one update per frame. Mousemove fires faster than the browser
     * paints, and each one sets state that re-renders the sheet, so resizing a
     * column on a large plan was doing several times more work than the screen
     * could ever show.
     */
    const onMove = (move: MouseEvent) => {
      latestX = move.clientX;
      if (isFrameQueued.current) return;
      isFrameQueued.current = true;
      requestAnimationFrame(() => {
        // Already flushed by a release that beat the frame.
        if (!isFrameQueued.current) return;
        isFrameQueued.current = false;
        apply();
      });
    };

    const onUp = () => {
      // Flushed rather than dropped: a pending frame holds the last movement, and
      // losing it would leave the column a few pixels short of where it was let go.
      if (isFrameQueued.current) {
        isFrameQueued.current = false;
        apply();
      }
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    // Set on the body, not the strip: during a drag the cursor is usually somewhere
    // else entirely, and without this it flickers back to a text caret over cells.
    document.body.style.cursor = 'col-resize';
    // eslint-disable-next-line i18next/no-literal-string -- CSS property value
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    // A button rather than a separator: it is operated, not just a divider, and the
    // slider role carries the width to assistive tech so it can be driven by keyboard.
    <button
      type="button"
      role="slider"
      aria-orientation="vertical"
      aria-label={`Resize ${label} column`}
      aria-valuenow={width}
      aria-valuemin={minWidth}
      aria-valuemax={MAX_COLUMN_WIDTH}
      data-testid={`column-resize-${label}`}
      onMouseDown={beginDrag}
      onDoubleClick={onReset}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') onResize(clampColumnWidth(width - KEYBOARD_STEP, minWidth));
        else if (event.key === 'ArrowRight')
          onResize(clampColumnWidth(width + KEYBOARD_STEP, minWidth));
        else return;
        // Or the sheet scrolls sideways under the column being resized.
        event.preventDefault();
      }}
      title={t('deliveryJobReview.columnResizeHandle.dragToResize')}
      className="absolute -right-2 -top-1.5 z-20 h-8 w-[7px] cursor-col-resize touch-none focus-visible:outline-none"
    >
      <span className="mx-auto block h-full w-[3px] rounded-full bg-transparent transition-colors hover:bg-primary" />
    </button>
  );
}
